import { createResizeObserverWithRAF, useIsomorphicLayoutEffect } from '@heidi/utils';
import * as React from 'react';

/**
 * Tracks the border-box width of a floating panel's trigger element through
 * React state, replacing Radix width variables that cause ResizeObserver loops.
 */
export function useTriggerWidth(trigger: HTMLElement | null): number | undefined {
  const [width, setWidth] = React.useState<number>();

  useIsomorphicLayoutEffect(() => {
    if (!trigger) {
      setWidth(undefined);
      return;
    }
    // Zero clears the width so the consumer's class fallback sizes the content.
    const apply = (w: number) => setWidth(w > 0 ? w : undefined);
    // offsetWidth, not the rect: a scale-animating ancestor understates the
    // rect mid-animation and no ResizeObserver fires afterwards to correct it.
    apply(trigger.offsetWidth);
    // The helper constructs a ResizeObserver eagerly; without the API the
    // throw would unmount the subtree instead of keeping the seeded width.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = createResizeObserverWithRAF(([entry]) => {
      if (!entry) return;
      // Not contentRect: that is the content box, which drops the trigger's
      // padding and border and renders the content narrower than its anchor.
      apply(entry.borderBoxSize?.[0]?.inlineSize ?? trigger.offsetWidth);
    });
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [trigger]);

  return width;
}
